import { ReactElement, memo } from 'react';
import { GetStoreByIdResult } from '@spa/lib/graphql/stores/getStoreById';
import MapCardComponent from '@spa/components/map-card/map-card.component';
import { STORE_QUERY_PARAM } from '@spa/lib/storeSlug';
import logger from '@spa/lib/logger';
import styles from './store-details.component.module.scss';
import useBrowserSearchParam from '@spa/lib/react/hooks/useBrowserSearchParam';
import useRuntimeSearchParams from '@spa/lib/react/hooks/useRuntimeSearchParams';
import { useStoresAPI } from '@spa/lib/react/hooks/useStoresAPI';

export interface Props {
  readonly componentId: string;
  readonly store: GetStoreByIdResult;
}

// The map card renders all of its Store Finder affordances as links on this page (isHomepage), so it
// never calls setShownStore; the links carry the search term themselves.
const noopSetShownStore = (): void => undefined;

function StoreDetailsComponent({ componentId, store }: Props): ReactElement {
  const { getMyStoreId, setMyStoreId } = useStoresAPI();
  const favoriteStoreId = getMyStoreId();
  // Search term carried over from the Store Finder (/s/{slug}?storeQuery=...), read from the browser
  // URL so the force-static /s route is not de-opted. Anonymous requests are served from the cached
  // /s route (storeQuery is anonymous-cacheable, see proxy.ts), where the server snapshot is empty and
  // the links pick up the term after hydration; on the dynamic route the runtime search params seed
  // the server snapshot, so the server-rendered links already carry it.
  const runtimeSearchTerm: string | null = useRuntimeSearchParams().get(STORE_QUERY_PARAM);
  const searchTerm: string | null = useBrowserSearchParam(STORE_QUERY_PARAM, runtimeSearchTerm);

  // The "select store" affordance is a link to the (searched) Store Finder, so only persist the store
  // here and let the link navigate — pushing as well would race a second navigation.
  // The promise is handled here (the prop is `() => void`), so a failed save is logged instead of
  // surfacing as an unhandled rejection while the link navigates away.
  const setSelectedStore = (): void => {
    setMyStoreId(store.code).catch((error: unknown) => {
      logger.error(error, 'Error setting my store:');
    });
  };

  return (
    <div>
      <div id={componentId} className={styles['storedetails']}>
        <MapCardComponent
          isProductAvailability={false}
          isHomepage={true}
          isSelectedStore={store.code === favoriteStoreId}
          product={null}
          store={store}
          storefinderSearchTerm={searchTerm}
          setShownStore={noopSetShownStore}
          setSelectedStore={setSelectedStore}
        />
      </div>
    </div>
  );
}

export default memo(StoreDetailsComponent);
